import omitProps from '@hocs/omit-props';
import withLifecycle from '@hocs/with-lifecycle';
import { func, string } from 'prop-types';
import { contains, filter, head, isEmpty, join, map, pluck, prop, propEq, reduce } from 'ramda';
import { injectIntl } from 'react-intl';
import { connect } from 'react-refetch';
import {
  compose,
  setDisplayName,
  setPropTypes,
  withHandlers,
  withProps,
  withPropsOnChange,
  withStateHandlers,
} from 'recompose';
import { Color } from '../../../constants/Color';
import { FilterName } from '../../../constants/FilterName';
import { Layout } from '../../../constants/Layout';
import { Orientation } from '../../../constants/Orientation';
import { Pricing } from '../../../constants/Pricing';
import { MainSource } from '../../../constants/Source';
import { WixMediaRoot } from '../../../constants/WixMediaRoot';
import { MEDIA_TYPE_TRANSLATIONS } from '../../../constants/MediaType';
import { generateRandomGuid4 } from '../../../critical/utils';
import { getLocale } from '../../env/getLocale';
import { FiltersBiClickOrigins } from '../../hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';
import { getAdapter } from '../../media-manager-sdk/getAdapter';
import { withSearchAndFiltersAnalytics } from '../bi/withSearchAndFiltersAnalytics';
import { ejectIntl } from '../ejectIntl';
import { withLayoutAndSort } from '../withLayoutAndSort';
import { withLoadingErrorCatcher } from '../withLoadingErrorCatcher';
import { withMediaManagerSDK } from '../withMediaManagerSDK';
import { withMMGRSettings, withoutMMGRSettings } from '../withMMGRSettings';
import {
  decodePublicMediaFolderId,
  encodeBiCategory,
  encodePublicMediaFolderId,
  getColorOverride,
  getColumnsCount,
  getPossiblePublicFolder,
} from './utils';
import { MAX_NUMBER_OF_ITEMS_IN_COLUMN, ROOT_TITLE_MAP } from './withPublicMedia.constants';
import {
  ICategories,
  IState,
  IWithPublicMediaInner,
  IWithPublicMediaOuter,
  MediaManagerSDKPublicMediaFolder,
  TStateUpdaters,
} from './withPublicMedia.types';
import { isCategoryOfNonVisualMediaType } from '../../publicMedia';

const emptyItemFilters = [];

export const withPublicMedia = compose<IWithPublicMediaInner, IWithPublicMediaOuter>(
  setPropTypes({
    source: string.isRequired,
    openFolderById: func.isRequired,
  }),
  setDisplayName('withPublicMedia'),
  withLoadingErrorCatcher,
  withLayoutAndSort({ layouts: [Layout.Gallery] }),
  withStateHandlers<IState, TStateUpdaters, IWithPublicMediaOuter>(
    { categories: {}, categoriesOrder: [] },
    {
      setCategories:
        () =>
        ({ categories, categoriesOrder }) => ({ categories, categoriesOrder }),
    },
  ),
  injectIntl,
  withPropsOnChange(['folderId'], ({ folderId, intl: { formatMessage } }) => ({
    decodedFolderId: decodePublicMediaFolderId(folderId),
    publicMediaOrientations: [
      { id: Orientation.All, name: formatMessage({ id: 'orientation.all' }) },
      { id: Orientation.Horizontal, name: formatMessage({ id: 'orientation.horizontal' }) },
      { id: Orientation.Vertical, name: formatMessage({ id: 'orientation.vertical' }) },
    ],
  })),
  withMediaManagerSDK({ source: MainSource.MediaFromWix }),
  withMMGRSettings,
  connect<any>(
    ({
      mmgrSettings,
      decodedFolderId,
      openFolderById,
      publicMediaFoldersTreeMP,
      setCategories,
      setLoadingError,
      isNTT,
      isYell,
      navigateToEnterpriseTab,
      enterpriseTab: enterpriseTabKey,
      intl: { formatMessage },
    }) => {
      const enterpriseTabs = mmgrSettings.enterpriseTabs;
      const enterpriseTab = enterpriseTabs && enterpriseTabs.find(propEq('tabNameKey', enterpriseTabKey));
      const isEnterpriseTabV1 = !!enterpriseTab && enterpriseTab.enterpriseVersion === 1;

      const publicMediaRoot = isNTT
        ? [WixMediaRoot.Ntt]
        : isYell
        ? [WixMediaRoot.Yell]
        : enterpriseTab
        ? [enterpriseTab.mediaRoot]
        : mmgrSettings.publicMediaRoot;

      return {
        publicFoldersTreeRequest: {
          value: () => {
            const publicMediaAdapter = getAdapter(MainSource.MediaFromWix, mmgrSettings);
            const privateSiteAdapter = getAdapter(MainSource.SiteFiles, mmgrSettings);
            const correlationIdFromPrivateAdapter = privateSiteAdapter.correlationId.get();

            if (correlationIdFromPrivateAdapter) {
              publicMediaAdapter.correlationId.set(correlationIdFromPrivateAdapter);
            } else {
              publicMediaAdapter.correlationId.randomize();
            }

            return publicMediaFoldersTreeMP(publicMediaRoot, {
              language: getLocale(),
              restrictContent: mmgrSettings.restrictContent,
              country: mmgrSettings.country,
              isEnterpriseTabV1,
              metaSiteToken: mmgrSettings.metaSiteToken,
            });
          },
          comparison: join(',', publicMediaRoot),
          andThen: (response: any) => {
            const categoriesList = reduce<MediaManagerSDKPublicMediaFolder, MediaManagerSDKPublicMediaFolder[]>(
              (categories, rootCategory) => [...categories, ...rootCategory.subFolders],
              [],
              response.data,
            );
            const categories = reduce<MediaManagerSDKPublicMediaFolder, ICategories>(
              (categories, category) => ({
                ...categories,
                [prop<string>('id', category)]: {
                  ...category,
                  ...(isEnterpriseTabV1
                    ? {
                        /*
                          enterprise tab v1 response doesn't provide applicable display names,
                          so generating those from media types
                        */
                        name: formatMessage({ id: MEDIA_TYPE_TRANSLATIONS[category.mediaType] }),
                      }
                    : {}),
                },
              }),
              {},
              categoriesList,
            );
            const categoriesOrder = pluck('id', categoriesList);

            let { categoryId, subCategoryId } = decodedFolderId;

            if (!categoryId || !categories[categoryId]) {
              categoryId = head(categoriesOrder);
            }

            const subCategories = categories[categoryId]?.subFolders || [];

            if (!subCategoryId || !getPossiblePublicFolder(subCategoryId, subCategories)) {
              subCategoryId = head(subCategories)?.id;
            }

            if (categoryId !== decodedFolderId.categoryId || subCategoryId !== decodedFolderId.subCategoryId) {
              const encodedFolder = encodePublicMediaFolderId({ ...decodedFolderId, categoryId, subCategoryId });
              if (enterpriseTabKey) {
                navigateToEnterpriseTab(enterpriseTabKey, encodedFolder);
              } else {
                const source = isNTT ? MainSource.NTT : isYell ? MainSource.Yell : MainSource.MediaFromWix;
                openFolderById(encodedFolder, source);
              }
            }

            setCategories({ categories, categoriesOrder });

            return {};
          },
          andCatch: (reason) => {
            setLoadingError(reason);

            return {};
          },
        },
      };
    },
  ),
  withProps(({ publicFoldersTreeRequest, categories, isEnterprise }) => ({
    isMediaFromWixFoldersLoading: publicFoldersTreeRequest.pending || (!isEnterprise && isEmpty(categories)),
    publicMediaRootCategories: categories,
  })),
  withPropsOnChange(['decodedFolderId', 'mmgrSettings'], ({ decodedFolderId, mmgrSettings }) => {
    const overriddenColor = getColorOverride(decodedFolderId.color);
    return {
      publicMediaFolderId: decodedFolderId.subCategoryId,
      publicMediaFilters: {
        orientation: decodedFolderId.orientation,
        color: overriddenColor ? `color_${decodedFolderId.color}` : Color.None,
        restrictContent: mmgrSettings.restrictContent,
        pricing: Pricing.Free,
        restrictK12: mmgrSettings.restrictK12,
      },
    };
  }),
  withSearchAndFiltersAnalytics,
  withHandlers({
    openFolderOrEnterpriseTab:
      ({ navigateToEnterpriseTab, enterpriseTab, openFolderById }) =>
      (folderId) =>
        enterpriseTab ? navigateToEnterpriseTab(enterpriseTab, folderId) : openFolderById(folderId),
  }),
  withHandlers({
    selectPublicMediaCategory:
      ({ categories, decodedFolderId, trackSearchAndFiltersStarted, openFolderOrEnterpriseTab }) =>
      (categoryId) => {
        if (decodedFolderId.categoryId !== categoryId) {
          const subCategoryId = head(categories[categoryId].subFolders).id;
          openFolderOrEnterpriseTab(
            encodePublicMediaFolderId({
              ...decodedFolderId,
              categoryId,
              subCategoryId,
              color: Color.Any,
              orientation: Orientation.All,
              pricing: Pricing.All,
            }),
          );

          trackSearchAndFiltersStarted({
            correlationId: generateRandomGuid4(),
            clickOrigin: FiltersBiClickOrigins[FilterName.Type],
            target: categoryId,
            filterType: encodeBiCategory(categoryId, categories),
            filterCategory: encodeBiCategory(categoryId, categories, subCategoryId),
            filterOrientation: Orientation.All,
            filterColor: Color.Any,
            filterPricing: Pricing.All,
          });
        }
      },
    selectPublicMediaSubCategory:
      ({ categories, decodedFolderId, openFolderOrEnterpriseTab, trackSearchAndFiltersStarted }) =>
      (subCategoryId) => {
        if (decodedFolderId.subCategoryId !== subCategoryId) {
          openFolderOrEnterpriseTab(
            encodePublicMediaFolderId({
              ...decodedFolderId,
              subCategoryId,
            }),
          );

          trackSearchAndFiltersStarted({
            correlationId: generateRandomGuid4(),
            clickOrigin: FiltersBiClickOrigins[FilterName.Category],
            target: subCategoryId,
            filterType: encodeBiCategory(decodedFolderId.categoryId, categories),
            filterCategory: encodeBiCategory(decodedFolderId.categoryId, categories, subCategoryId),
            filterOrientation: decodedFolderId.orientation,
            filterColor: decodedFolderId.color,
            filterPricing: Pricing.Free,
          });
        }
      },
    setOrientation:
      ({ openFolderOrEnterpriseTab, categories, decodedFolderId, trackSearchAndFiltersStarted }) =>
      (orientation) => {
        if (decodedFolderId.orientation !== orientation) {
          openFolderOrEnterpriseTab(encodePublicMediaFolderId({ ...decodedFolderId, orientation }));

          trackSearchAndFiltersStarted({
            correlationId: generateRandomGuid4(),
            clickOrigin: FiltersBiClickOrigins[FilterName.Orientation],
            target: orientation,
            filterType: encodeBiCategory(decodedFolderId.categoryId, categories),
            filterCategory: encodeBiCategory(decodedFolderId.categoryId, categories, decodedFolderId.subCategoryId),
            filterOrientation: orientation,
            filterColor: decodedFolderId.color,
            filterPricing: Pricing.Free,
          });
        }
      },
    setColor:
      ({ categories, openFolderOrEnterpriseTab, decodedFolderId, trackSearchAndFiltersStarted }) =>
      (color) => {
        if (decodedFolderId.color !== color) {
          openFolderOrEnterpriseTab(encodePublicMediaFolderId({ ...decodedFolderId, color }));

          trackSearchAndFiltersStarted({
            correlationId: generateRandomGuid4(),
            clickOrigin: FiltersBiClickOrigins[FilterName.Color],
            target: color,
            filterType: encodeBiCategory(decodedFolderId.categoryId, categories),
            filterCategory: encodeBiCategory(decodedFolderId.categoryId, categories, decodedFolderId.subCategoryId),
            filterOrientation: decodedFolderId.orientation,
            filterColor: color,
            filterPricing: Pricing.Free,
          });
        }
      },
    clearAllExtendedFilters:
      ({ openFolderOrEnterpriseTab, decodedFolderId }) =>
      () => {
        openFolderOrEnterpriseTab(
          encodePublicMediaFolderId({
            categoryId: decodedFolderId.categoryId,
            subCategoryId: decodedFolderId.subCategoryId,
          }),
        );
      },
  }),
  injectIntl,
  withPropsOnChange(
    ['decodedFolderId', 'isMediaFromWixFoldersLoading', 'categories', 'categoriesOrder', 'mmgrSettings'],
    ({
      decodedFolderId: { categoryId, subCategoryId, orientation, color },
      isMediaFromWixFoldersLoading,
      categories,
      categoriesOrder,
      setOrientation,
      setColor,
      selectPublicMediaCategory,
      selectPublicMediaSubCategory,
      publicMediaOrientations,
      intl: { formatMessage },
      mmgrSettings,
    }) => {
      const currentCategory = categories[categoryId];

      if (!currentCategory) {
        return { itemsFilters: emptyItemFilters };
      }

      const { subFolders: subCategories, parentId: publicMediaRoot } = currentCategory;

      const enterpriseTab = mmgrSettings.enterpriseTabs?.find((x) => x.mediaRoot === publicMediaRoot);

      return {
        itemsFilters: isMediaFromWixFoldersLoading
          ? emptyItemFilters
          : filter(Boolean, [
              {
                name: FilterName.Type,
                activeItemId: categoryId,
                menuTitle: formatMessage({ id: 'free-from-wix.filter.types.title' }),
                menuItems: map<string, MediaManagerSDKPublicMediaFolder>(
                  (categoryId) => categories[categoryId],
                  categoriesOrder,
                ),
                columnsCount: getColumnsCount(categoriesOrder.length, MAX_NUMBER_OF_ITEMS_IN_COLUMN),
                onItemClick: (categoryId) => () => selectPublicMediaCategory(categoryId),
              },
              isCategoryOfNonVisualMediaType(currentCategory)
                ? null
                : {
                    name: FilterName.Category,
                    activeItemId: subCategoryId,
                    menuTitle: formatMessage({ id: 'free-from-wix.filter.categories.title' }),
                    menuItems: subCategories,
                    columnsCount: getColumnsCount(subCategories.length, MAX_NUMBER_OF_ITEMS_IN_COLUMN),
                    onItemClick: (subCategoryId) => () => selectPublicMediaSubCategory(subCategoryId),
                  },
              isCategoryOfNonVisualMediaType(currentCategory) ||
              contains(publicMediaRoot, [
                WixMediaRoot.Videos,
                WixMediaRoot.VideosPlus,
                WixMediaRoot.TransparentVideos,
                WixMediaRoot.SocialIcons,
                WixMediaRoot.FileIcons,
                WixMediaRoot.VectorArt,
                WixMediaRoot.VectorArtNew,
                WixMediaRoot.BasicShapes,
                WixMediaRoot.BaseUI,
                WixMediaRoot.Dividers,
              ])
                ? null
                : {
                    name: FilterName.Orientation,
                    activeItemId: orientation,
                    activeItemLabel:
                      orientation === Orientation.All ? formatMessage({ id: 'orientation.orientation' }) : null,
                    menuTitle: formatMessage({ id: 'free-from-wix.filter.orientation.title' }),
                    menuItems: publicMediaOrientations,
                    columnsCount: -1,
                    onItemClick: (orientation) => () => setOrientation(orientation),
                  },
              contains(publicMediaRoot, [
                WixMediaRoot.Images,
                WixMediaRoot.Illustrations,
                WixMediaRoot.SocialIcons,
                WixMediaRoot.Ntt,
              ])
                ? {
                    name: FilterName.Color,
                    activeItemId: color,
                    activeItemLabel: formatMessage({ id: 'filter.color.color' }),
                    menuTitle: formatMessage({ id: 'filter.color.title' }),
                    options: {
                      isAnyColorFilterVisible: true,
                      isBlackWhiteFilterVisible: true,
                      customColors: [
                        Color.Red,
                        Color.Orange,
                        Color.Yellow,
                        Color.Green,
                        Color.Cyan,
                        Color.Blue,
                        Color.Purple,
                        Color.Magenta,
                        Color.Brown,
                        Color.White,
                        Color.Grey,
                        Color.Black,
                      ],
                    },
                    onItemClick: (color) => () => setColor(color),
                  }
                : null,
            ]),
        title: enterpriseTab
          ? formatMessage(
              {
                id: 'enterprise-tab.title',
              },
              { tabName: enterpriseTab.tabNameEn },
            )
          : formatMessage({ id: ROOT_TITLE_MAP[publicMediaRoot] || 'free-from-wix.title' }),
        publicMediaMediaType: currentCategory.mediaType,
        publicMediaCurrentCategory: currentCategory,
        demonstrationThumbnailStyle: contains(currentCategory.parentId, [
          WixMediaRoot.VectorArt,
          WixMediaRoot.VectorArtNew,
          WixMediaRoot.BasicShapes,
        ])
          ? { backgroundSize: 'contain' }
          : null,
      };
    },
  ),
  withoutMMGRSettings,
  ejectIntl,
  withLifecycle({
    onDidUpdate({ decodedFolderId: prevDecodedFolderId }, { decodedFolderId, openFolderOrEnterpriseTab }) {
      const prevEncodedFolderId = encodePublicMediaFolderId(prevDecodedFolderId);

      if (
        prevEncodedFolderId !== encodePublicMediaFolderId(decodedFolderId) &&
        (!decodedFolderId.categoryId || !decodedFolderId.subCategoryId)
      ) {
        openFolderOrEnterpriseTab(prevEncodedFolderId);
      }
    },
  }),
  omitProps('publicFoldersTreeRequest'),
);
